import {Alert, Icon, Tooltip} from "antd";
import React, {CSSProperties, useContext} from "react";
import {isTalosCustomer} from "../../common/constants/organizations";
import {OrgDataContext} from "../../context/orgData";
import {
    assessSafetyCard,
    SafetyCardAssessment,
    SafetyCardCredential
} from "./safety-card-status";

type SafetyCardPassenger = {
    _id?: string,
    firstName?: string,
    lastName?: string,
    personID?: {
        credentials?: SafetyCardCredential[] | null
    } | null
}

export interface SafetyCardAlertProps {
    passengers?: SafetyCardPassenger[] | null,
    customerID?: string | null,
    style?: CSSProperties
}

type AffectedPassenger = {
    passenger: SafetyCardPassenger,
    assessment: SafetyCardAssessment
}

function getPassengerName(passenger: SafetyCardPassenger): string {
    return [passenger.firstName, passenger.lastName].filter(Boolean).join(' ')
        || passenger._id
        || 'Unknown passenger';
}

function getStatusText(assessment: SafetyCardAssessment): string {
    return assessment.status === 'expired'
        ? `Expired ${assessment.expirationDate}`
        : 'Missing';
}

const SafetyCardAlert: React.FC<SafetyCardAlertProps> = ({passengers, customerID, style}) => {
    const orgData = useContext(OrgDataContext);

    if (!isTalosCustomer(customerID || orgData?.customerID)) return null;

    const seenPassengerIDs = new Set<string>();
    const affectedPassengers = (passengers || []).reduce<AffectedPassenger[]>((affected, passenger, index) => {
        const passengerID = passenger._id || `passenger-${index}`;
        if (seenPassengerIDs.has(passengerID)) return affected;
        seenPassengerIDs.add(passengerID);

        const assessment = assessSafetyCard(passenger.personID?.credentials);
        if (assessment.status === 'missing' || assessment.status === 'expired') {
            affected.push({passenger, assessment});
        }

        return affected;
    }, []);

    if (!affectedPassengers.length) return null;

    const count = affectedPassengers.length;
    const message = count === 1
        ? '1 passenger has a missing or expired Safety Card.'
        : `${count} passengers have missing or expired Safety Cards.`;

    const details = (
        <div style={{maxHeight: 300, overflowY: 'auto'}}>
            {affectedPassengers.map(({passenger, assessment}, index) => (
                <div key={passenger._id || index}>
                    <strong>{getPassengerName(passenger)}</strong>: {getStatusText(assessment)}
                </div>
            ))}
        </div>
    );

    return (
        <Alert
            type="warning"
            showIcon
            style={style}
            message={(
                <span>
                    {message}
                    <Tooltip title={details} overlayStyle={{maxWidth: 400}}>
                        <span
                            role="button"
                            tabIndex={0}
                            style={{cursor: 'help', marginLeft: 8}}
                        >
                            <Icon type="info-circle" /> View affected passengers
                        </span>
                    </Tooltip>
                </span>
            )}
        />
    );
}

export default SafetyCardAlert;
